import React from "react";
import {
  Alert,
  Box,
  Button,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import TodayRoundedIcon from "@mui/icons-material/TodayRounded";
import { ownerApi } from "../../../api/owner";
import { formatApiError } from "../../../api/client";
import DashboardLoadingState from "../../../components/dashboard/DashboardLoadingState";
import OwnerWorkspaceNavigation from "./components/OwnerWorkspaceNavigation";
import OwnerMonthGrid from "./calendar/OwnerMonthGrid";
import OwnerDaySheet from "./calendar/OwnerDaySheet";
import { OwnerCalendarSettingsDialog, OwnerRoutineDialog } from "./calendar/OwnerCalendarDialogs";
import { OwnerCommitmentDialog, OwnerFinanceDialog } from "./center/OwnerQuickDialogs";
import OwnerRecurringFromTransactionDialog from "./center/OwnerRecurringFromTransactionDialog";
import { compactActionSx } from "./center/ownerCenterUi";

function localYmd(value = new Date()) {
  const date = new Date(value.getTime() - value.getTimezoneOffset() * 60000);
  return date.toISOString().slice(0, 10);
}

function shiftMonth(month, delta) {
  const [year, monthNumber] = String(month).split("-").map(Number);
  const date = new Date(year, monthNumber - 1 + delta, 1, 12, 0, 0, 0);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
}

function monthLabel(month) {
  const [year, monthNumber] = String(month).split("-").map(Number);
  return new Intl.DateTimeFormat("es-AR", { month: "long", year: "numeric" }).format(new Date(year, monthNumber - 1, 1));
}

export default function OwnerCalendarPage() {
  const today = React.useMemo(() => localYmd(), []);
  const [selectedDate, setSelectedDate] = React.useState(today);
  const [month, setMonth] = React.useState(today.slice(0, 7));
  const [calendar, setCalendar] = React.useState(null);
  const [accounts, setAccounts] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const [dialog, setDialog] = React.useState("");
  const [transaction, setTransaction] = React.useState(null);

  const load = React.useCallback(async () => {
    setError("");
    try {
      const [calendarResponse, accountResponse] = await Promise.all([
        ownerApi.calendar({ month, date: selectedDate }),
        ownerApi.listFinanceAccounts(),
      ]);
      setCalendar(calendarResponse.calendar);
      setAccounts(accountResponse.accounts || []);
    } catch (err) {
      setError(formatApiError(err, "No pude cargar tu calendario Owner."));
    } finally {
      setLoading(false);
    }
  }, [month, selectedDate]);

  React.useEffect(() => { load(); }, [load]);

  const selectDate = (date) => {
    setSelectedDate(date);
    if (date.slice(0, 7) !== month) setMonth(date.slice(0, 7));
  };

  const moveMonth = (delta) => {
    const next = shiftMonth(month, delta);
    setMonth(next);
    setSelectedDate(`${next}-01`);
  };

  const closeDialog = () => { setDialog(""); setTransaction(null); };
  const initialScheduledAt = `${selectedDate}T${calendar?.settings?.workdayStart || "09:00"}`;

  if (loading && !calendar) return <DashboardLoadingState label="Armando tu agenda Owner…" minHeight={520} />;

  return (
    <Box sx={{ pb: 5, maxWidth: 1740, mx: "auto" }}>
      <OwnerWorkspaceNavigation />
      <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" alignItems={{ md: "center" }} spacing={1.2} sx={{ mb: 1.35 }}>
        <Box><Typography variant="h4" sx={{ fontWeight: 1000, letterSpacing: "-.04em" }}>Calendario</Typography><Typography sx={{ color: "text.secondary", fontWeight: 700, mt: .25 }}>Tu hoja de operación diaria: tiempo disponible, tareas, rutinas y dinero real.</Typography></Box>
        <Stack direction="row" spacing={.45} alignItems="center"><IconButton onClick={() => moveMonth(-1)}><ChevronLeftRoundedIcon /></IconButton><Typography sx={{ minWidth: 155, textAlign: "center", textTransform: "capitalize", fontWeight: 950 }}>{monthLabel(month)}</Typography><IconButton onClick={() => moveMonth(1)}><ChevronRightRoundedIcon /></IconButton><Button startIcon={<TodayRoundedIcon />} onClick={() => { setMonth(today.slice(0,7)); setSelectedDate(today); }} sx={compactActionSx}>Hoy</Button></Stack>
      </Stack>
      {error ? <Alert severity="error" sx={{ mb: 1.3 }}>{error}</Alert> : null}

      {calendar ? <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", xl: "minmax(560px,.88fr) minmax(620px,1.12fr)" }, gap: 1.35, alignItems: "start" }}><OwnerMonthGrid month={calendar.month} days={calendar.days || []} selectedDate={calendar.selectedDate} onSelectDate={selectDate} /><OwnerDaySheet calendar={calendar} onNewTask={() => setDialog("task")} onNewRoutine={() => setDialog("routine")} onSettings={() => setDialog("settings")} onNewMovement={() => setDialog("finance")} onMakeRecurring={(row) => { setTransaction(row); setDialog("recurring"); }} onChanged={load} /></Box> : null}

      <OwnerCommitmentDialog open={dialog === "task"} onClose={closeDialog} onSaved={load} initialScheduledAt={initialScheduledAt} initiatives={[]} />
      <OwnerRoutineDialog open={dialog === "routine"} onClose={closeDialog} selectedDate={selectedDate} onSaved={load} />
      <OwnerCalendarSettingsDialog open={dialog === "settings"} onClose={closeDialog} settings={calendar?.settings} onSaved={load} />
      <OwnerFinanceDialog open={dialog === "finance"} onClose={closeDialog} accounts={accounts} onSaved={load} />
      <OwnerRecurringFromTransactionDialog open={dialog === "recurring"} onClose={closeDialog} transaction={transaction} recurring={calendar?.recurring} onSaved={load} />
    </Box>
  );
}
